const debounce = require('lodash/debounce');
const isMatchedBreakpoint = require('../common/utils/isMatchedBreakpoint');

const bottomSheetLimitBreakpoint = 'bp_1025';
const debounceDelay = 500;

let defaultSelectors = {
  bottomSheet: '.bottom-sheet',
  bottomSheetInitialized: 'bottom-sheet--initialized',
  bottomSheetFull: 'bottom-sheet--fullscreen',
  bottomSheetDragging: 'bottom-sheet--dragging',
  bottomSheetInteraction: 'interaction--bounce-top',
  bottomSheetHeader: '.bottom-sheet__header',
  bottomSheetInfo: 'bottom-sheet__info',
  bodyScrollDisabled: 'u-scroll-disabled',
};
let sheetHeight = 20; /* Sheet initial height on vh */
let sheetInitialHeight = 0; /* Sheet initial height on vh including or not button */
let dragPosition;
let sheet = document.querySelector(defaultSelectors.bottomSheet);
let sheetHeader = sheet?.querySelector(defaultSelectors.bottomSheetHeader);
const body = document.querySelector('body');

/**
 * Sets the visible height of the bottom sheet.
 * @param {number} value - The height value in percentage.
 */
function setSheetHeight(value) {
  sheetHeight = Math.max(0, Math.min(100, value));
  sheet.style.transform = `translateY(-${sheetHeight}%)`;

  if (sheetHeight >= 30) {
    sheet.classList.add(defaultSelectors.bottomSheetFull);
  } else {
    sheet.classList.remove(defaultSelectors.bottomSheetFull);
  }
}

/**
 * Sets the initial visible height of the bottom sheet.
 * @param {number} height - The height of the sheet header.
 */
function setInitialSheetHeight(height) {
  const sheetHeaderHeight = height || sheetHeader.clientHeight;
  const sheetComputedStyles = getComputedStyle(sheet);
  const visibleHeaderSize =
    sheetHeaderHeight + parseInt(sheetComputedStyles.paddingTop, 10);
  sheet.style.transform = `translateY(-${visibleHeaderSize}px)`;
}

/**
 * Gets the touch position from an event.
 * @param {TouchEvent|MouseEvent} event
 * @returns {Touch|MouseEvent}
 */
function touchPosition(event) {
  return event.touches ? event.touches[0] : event;
}

/**
 * Handles the drag start event for the bottom sheet.
 * @param {Event} event
 */
function onDragStart(event) {
  if (event.target.tagName !== 'BUTTON') {
    dragPosition = touchPosition(event).pageY;
    sheet.classList.add(defaultSelectors.bottomSheetDragging);
    body.classList.add(defaultSelectors.bodyScrollDisabled);
  } else {
    dragPosition = undefined;
  }
}

/**
 * Handles the drag move event for the bottom sheet.
 * @param {Event} event
 */
function onDragMove(event) {
  if (dragPosition === undefined) return;

  const y = touchPosition(event).pageY;
  const deltaY = dragPosition - y;
  const deltaHeight = (deltaY / window.innerHeight) * 100;

  setSheetHeight(sheetHeight + deltaHeight);
  dragPosition = y;
}

/**
 * On drag end, if the movement exceeds 50% of the screen, the sheet must be at fullscreen
 */
function onDragEnd() {
  if (dragPosition) {
    sheet.classList.remove(defaultSelectors.bottomSheetDragging);
    if (sheetHeight > 50) {
      setSheetHeight(100);
    } else {
      // It is important to first remove classes & then set initial height to avoid unwanted calculation results
      sheet.classList.remove(defaultSelectors.bottomSheetFull);
      body.classList.remove(defaultSelectors.bodyScrollDisabled);
      setInitialSheetHeight();
    }
  }
  dragPosition = undefined;
}

/**
 * Handles click events on the bottom sheet.
 * On click on sheet, the sheet should be opened
 * @param {MouseEvent} e
 */
function onClick(e) {
  const isFullScreen = sheet.classList.contains(
    defaultSelectors.bottomSheetFull
  );
  if (isFullScreen) {
    const isBottomSheetInfo = e.target.classList.contains(
      defaultSelectors.bottomSheetInfo
    );
    if (isBottomSheetInfo) {
      setInitialSheetHeight(sheetInitialHeight);
      sheet.classList.remove(defaultSelectors.bottomSheetFull);
      body.classList.remove(defaultSelectors.bodyScrollDisabled);
    }
  } else {
    setSheetHeight(100);
  }
}

/**
 * Adds event listeners for bottom sheet interactions.
 */
function addListeners() {
  sheet.addEventListener('mousedown', onDragStart);
  sheet.addEventListener('touchstart', onDragStart);
  sheet.addEventListener('click', onClick);

  window.addEventListener('mousemove', onDragMove);
  window.addEventListener('touchmove', onDragMove);

  window.addEventListener('mouseup', onDragEnd);
  window.addEventListener('touchend', onDragEnd);
}

/**
 * Removes event listeners for bottom sheet interactions.
 */
function removeListeners() {
  sheet.removeEventListener('mousedown', onDragStart);
  sheet.removeEventListener('touchstart', onDragStart);
  sheet.removeEventListener('click', onClick);

  window.removeEventListener('mousemove', onDragMove);
  window.removeEventListener('touchmove', onDragMove);

  window.removeEventListener('mouseup', onDragEnd);
  window.removeEventListener('touchend', onDragEnd);
}

/**
 * Creates and initializes the bottom sheet.
 */
function create() {
  sheet.classList.add(defaultSelectors.bottomSheetInitialized);
  sheetInitialHeight = sheetHeader.clientHeight;
  setInitialSheetHeight();
  addListeners();
  sheet.classList.add(defaultSelectors.bottomSheetInteraction);
}

/**
 * Destroys the bottom sheet and cleans up listeners.
 */
function destroy() {
  sheet.classList.remove(defaultSelectors.bottomSheetInitialized);
  setSheetHeight(0);
  removeListeners();
}

/**
 * Initializes the bottom sheet with custom selectors.
 * @param {Object} selectors - Custom selectors to override defaults.
 */
function initialize(selectors) {
  defaultSelectors = { ...defaultSelectors, ...selectors };
  sheet = document.querySelector(defaultSelectors.bottomSheet);
  sheetHeader = document.querySelector(defaultSelectors.bottomSheetHeader);

  if (sheet && sheetHeader) {
    if (isMatchedBreakpoint.matches(bottomSheetLimitBreakpoint)) {
      create();
    }

    // eslint-disable-next-line listeners/no-missing-remove-event-listener
    window.addEventListener(
      'resize',
      debounce(() => {
        if (isMatchedBreakpoint.matches(bottomSheetLimitBreakpoint)) {
          if (
            !sheet.classList.contains(defaultSelectors.bottomSheetInitialized)
          ) {
            create();
          }
        } else if (
          sheet.classList.contains(defaultSelectors.bottomSheetInitialized)
        ) {
          destroy();
        }
      }, debounceDelay)
    );

    const { checkoutEventBus } = require('../checkout/events');
    checkoutEventBus.subscribe(
      checkoutEventBus.EVENTS.checkoutStageChanged,
      () => {
        // It is important to first remove fullpage class & then set initial height to avoid unwanted calculation results
        const isFullScreen = sheet.classList.contains(
          defaultSelectors.bottomSheetFull
        );
        if (isFullScreen) {
          sheet.classList.remove(defaultSelectors.bottomSheetFull);
          body.classList.remove(defaultSelectors.bodyScrollDisabled);
        }

        sheetInitialHeight = sheetHeader.clientHeight;
        setInitialSheetHeight(sheetInitialHeight);
      }
    );
  }
}

module.exports = {
  initialize,
};
